import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiCard,
  MiContentDrawer,
  MiDetailFields,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiNoteTextOutline } from "@mdi/js";
import { ActivityTimeline } from "@mibuildweb/components";
import { Box, Typography, useTheme } from "@mui/material";
import { MaterialCard } from "../../MaterialCard/MaterialCard";
import { useAddNoteDrawerViewModel } from "./useAddNoteDrawerViewModel";

const schema = z.object({
  fieldNote: z
    .string()
    .max(63, "Field note can't be more than 64 characters long")
    .optional(),
  notes: z
    .string()
    .max(1023, "Internal note can't be more than 1024 characters long")
    .optional(),
});

export function AddNoteDrawer({
  open,
  onClose,
  item,
  area,
  planDetails,
  updateArea,
  defaultFieldNote,
}) {
  const {
    formState: { dirtyFields },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      fieldNote: "",
      notes: "",
    },
    resolver: zodResolver(schema),
  });
  const { classes } = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      getNotes();
      reset({ fieldNote: defaultFieldNote || "", notes: "" });
    }
  }, [open]);

  const handleCloseDrawer = () => {
    onClose();
    reset();
  };

  const { getNotes, notes, handleCreateNote, isLoading } =
    useAddNoteDrawerViewModel({
      dirtyFields,
      getValues,
      planDetails,
      area,
      item,
      updateArea,
      handleCloseDrawer,
    });

  const fieldNoteField = [
    {
      name: "fieldNote",
      placeholder: "Add Field Note",
      formControl: control,
      padding: 4,
      inputProps: { maxLength: 64 },
      multiline: true,
      rows: 3,
    },
  ];

  const internalNoteField = [
    {
      name: "notes",
      placeholder: "Add Internal Notes",
      formControl: control,
      padding: 4,
      inputProps: { maxLength: 1024 },
      multiline: true,
      rows: 3,
    },
  ];

  const activityDetails = (activity) => {
    return (
      <MiCard
        key={activity.id}
        title={
          activity.activityType === "FieldNoteUpdate"
            ? "Field Note"
            : "Internal Note"
        }
        subtitle={activity.createdByUser?.displayName}
        leftIcon={mdiNoteTextOutline}
        inverse={activity.activityType !== "FieldNoteUpdate"}
        containerStyles={{
          marginTop: theme.spacing(2),
          padding: theme.spacing(2),
        }}
      >
        <Box className={classes.customBoxContainer}>
          <Typography variant="subtitle2" className={classes.notesText}>
            {activity.notes}
          </Typography>
        </Box>
      </MiCard>
    );
  };

  const containerStyles = {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
  };

  return (
    <MiContentDrawer
      open={open}
      variant="temporary"
      onClose={handleCloseDrawer}
      title="Add Note to Item"
      leftIcon={mdiNoteTextOutline}
      isLoading={isLoading}
      actions={[
        {
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          name: `Cancel`,
          onClick: handleCloseDrawer,
        },
        {
          variant: BUTTON_VARIANTS.CONTAINED,
          name: `Save`,
          onClick: handleCreateNote,
          disabled: !dirtyFields.fieldNote && !dirtyFields.notes,
        },
      ]}
    >
      <Box className={classes.drawerContentContainer}>
        <Box className={classes.innerBoxContainer}>
          <MaterialCard item={item} />
        </Box>
        <MiCard
          title="Field Note"
          leftIcon={mdiNoteTextOutline}
          containerStyles={containerStyles}
        >
          <MiDetailFields detailFields={fieldNoteField} />
        </MiCard>
        <MiCard
          title="Add Internal Notes"
          leftIcon={mdiNoteTextOutline}
          containerStyles={containerStyles}
          inverse
        >
          <MiDetailFields detailFields={internalNoteField} />
        </MiCard>
        <ActivityTimeline data={notes} renderDetail={activityDetails} />
      </Box>
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  customBoxContainer: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(0, 1),
    marginTop: theme.spacing(2),
    borderRadius: 4,
  },
  drawerContentContainer: {
    width: "100%",
  },
  innerBoxContainer: {
    padding: theme.spacing(0, 2),
  },
  notesText: {
    wordWrap: "break-word",
  },
}));
